import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import { ClipboardList, Check, X, Clock3, MapPin, CalendarDays, Phone, UserRound, RotateCcw, Trash2, Plus, ChevronDown, Upload } from "lucide-react";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const OUTING_PURPOSES = [
  "Medical appointment",
  "Health issue",
  "Tournament",
  "Training",
  "School Program",
  "Outing",
  "Family Event",
  "Family Emergency",
  "Official/Administrative Matters",
  "Student Welfare/Well-being",
  "Others",
];

const blankForm = {
  student_name: "",
  dorm_location: "",
  parent_name: "",
  parent_phone: "",
  out_start_date: "",
  out_end_date: "",
  out_time: "",
  duration_days: "1",
  purpose: "",
  description: "",
  attachment: "",
  attachment_name: "",
};
const blankBulkStudent = { student_name: "", dorm_location: "" };

const isApprover = (user) => user?.staff_role === "Warden";

function OutingSummary({ outing }) {
  const startDate = outing.out_start_date || outing.out_date;
  const endDate = outing.out_end_date || startDate;
  return (
    <div className="mt-2 space-y-1 text-sm">
      <p className="flex items-center gap-2 text-muted-foreground"><CalendarDays className="h-4 w-4 shrink-0" />{startDate === endDate ? startDate : `${startDate} – ${endDate}`} at {outing.out_time}</p>
      <p className="whitespace-pre-wrap"><span className="font-medium">Purpose:</span> {outing.purpose}</p>
      {outing.description && <p className="whitespace-pre-wrap"><span className="font-medium">Description:</span> {outing.description}</p>}
    </div>
  );
}

function OutingDetails({ outing, showParentPhone = true, requesterName, reviewedBy, rejectionNote, onDownloadAttachment }) {
  return (
    <details className="group mt-3 border-t border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between py-3 text-sm font-medium [&::-webkit-details-marker]:hidden">
        More details
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="grid gap-x-6 gap-y-2 pb-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <p className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />Dorm: {outing.dorm_location}</p>
        <p className="flex items-center gap-2"><UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />Parent: {outing.parent_name}</p>
        {showParentPhone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 shrink-0 text-muted-foreground" />{outing.parent_phone}</p>}
        <p className="flex items-center gap-2"><Clock3 className="h-4 w-4 shrink-0 text-muted-foreground" />{outing.duration_days} {Number(outing.duration_days) === 1 ? "day" : "days"}</p>
        {requesterName && <p>Requested by {requesterName}</p>}
        {reviewedBy && <p>Reviewed by {reviewedBy}</p>}
        {rejectionNote && <p className="sm:col-span-2 lg:col-span-3 text-muted-foreground">{rejectionNote}</p>}
        {outing.attachment_name && onDownloadAttachment && (
          <Button type="button" variant="link" className="h-auto justify-start p-0" onClick={() => onDownloadAttachment(outing)}>
            <Upload className="mr-1 h-4 w-4" />Proof: {outing.attachment_name}
          </Button>
        )}
      </div>
    </details>
  );
}

export default function StudentOuting() {
  const { user } = useAuth();
  const [form, setForm] = useState(blankForm);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkStudents, setBulkStudents] = useState([{ ...blankBulkStudent }, { ...blankBulkStudent }]);
  const [outings, setOutings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [outingRules, setOutingRules] = useState("");
  const [outingRulesVersion, setOutingRulesVersion] = useState(null);
  const [rulesDraft, setRulesDraft] = useState("");
  const [rulesEditorOpen, setRulesEditorOpen] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const approver = isApprover(user);
  const outingManager = user?.role === "admin" || approver;
  const canEditRules = user?.role === "admin" || user?.staff_role === "Hostel Supervisor";
  const isVisitor = user?.role === "visitor" || user?.staff_role === "Visitor";
  const requiresMedicalProof = ["Medical appointment", "Health issue"].includes(form.purpose);

  const loadOutings = async () => {
    try {
      const response = await api.get("/outings");
      setOutings(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load outing requests");
    }
  };

  const loadOutingRules = async () => {
    try {
      const response = await api.get("/outing-rules");
      setOutingRules(response.data?.rules || "");
      setOutingRulesVersion(response.data?.updated_at || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load outing rules");
    }
  };

  useEffect(() => {
    loadOutings();
    loadOutingRules();
  }, []);

  const submitRequest = async (termsAccepted) => {
    setSubmitting(true);
    try {
      const request = {
        ...form,
        out_date: form.out_start_date,
        duration_days: Number(form.duration_days),
        terms_accepted: termsAccepted,
        terms_version: outingRulesVersion,
      };
      if (bulkMode) {
        const { student_name, dorm_location, ...sharedDetails } = request;
        await api.post("/outings/bulk", { ...sharedDetails, students: bulkStudents });
      } else {
        await api.post("/outings", request);
      }
      setForm(blankForm);
      setBulkStudents([{ ...blankBulkStudent }, { ...blankBulkStudent }]);
      toast.success(termsAccepted ? "Outing request submitted for approval" : "Outing request declined and marked rejected");
      setRequestDialogOpen(false);
      setTermsOpen(false);
      await loadOutings();
    } catch (error) {
      if (error.response?.status === 409) await loadOutingRules();
      toast.error(error.response?.data?.detail || "Unable to submit outing request");
    } finally {
      setSubmitting(false);
    }
  };

  const chooseAttachment = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      event.target.value = "";
      setAttachmentError("File must not exceed 5 MB");
      return;
    }
    if (!(file.type === "application/pdf" || file.type.startsWith("image/")) || file.type === "image/svg+xml") {
      event.target.value = "";
      setAttachmentError("Choose a PDF or image file");
      return;
    }
    setAttachmentError("");
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, attachment: reader.result, attachment_name: file.name }));
    reader.onerror = () => setAttachmentError("Unable to read this file");
    reader.readAsDataURL(file);
  };

  const downloadOutingAttachment = async (outing) => {
    try {
      const response = await api.get(`/outings/${outing.id}/attachment`, { responseType: "blob" });
      const fileUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = outing.attachment_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to download proof attachment");
    }
  };

  const saveOutingRules = async () => {
    setSavingRules(true);
    try {
      const response = await api.put("/outing-rules", { rules: rulesDraft });
      setOutingRules(response.data.rules);
      setOutingRulesVersion(response.data.updated_at || null);
      setRulesEditorOpen(false);
      toast.success("Outing rules saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save outing rules");
    } finally {
      setSavingRules(false);
    }
  };

  const decide = async (outing, decision) => {
    try {
      await api.put(`/outings/${outing.id}/decision`, { decision });
      toast.success(`Outing ${decision.toLowerCase()}`);
      await loadOutings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update outing request");
    }
  };

  const revertApproval = async (outing) => {
    try {
      await api.put(`/outings/${outing.id}/revert`);
      toast.success("Outing approval reverted to pending");
      await loadOutings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to revert outing approval");
    }
  };

  const removeOuting = async (outing) => {
    const requestType = outing.status === "Rejected" ? "rejected outing request" : "approved outing";
    if (!window.confirm(`Remove the ${requestType} for ${outing.student_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/outings/${outing.id}`);
      toast.success("Outing request removed");
      await loadOutings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove outing");
    }
  };

  const approved = outings.filter((outing) => outing.status === "Approved");
  const pending = outings.filter((outing) => outing.status === "Pending");
  const rejected = outings.filter((outing) => outing.status === "Rejected");

  return (
    <div className="space-y-8" data-testid="student-outing-page">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Hostel Services</p>
        <h1 className="mt-1 flex items-center gap-3 font-display text-3xl font-extrabold">
          <ClipboardList className="h-8 w-8 text-primary" /> Student Outing
        </h1>
      </header>

      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogTrigger asChild>
          <Button data-testid="new-outing-request"><Plus className="mr-2 h-4 w-4" /> New outing request</Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Outing request form</DialogTitle>
            <DialogDescription>Submit one or multiple student outing requests for Warden approval.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => {
            event.preventDefault();
            if (requiresMedicalProof && !form.attachment) {
              setAttachmentError("Attach a medical appointment card or MC / sick leave certificate");
              return;
            }
            setAttachmentError("");
            setTermsOpen(true);
          }} className="grid gap-4 sm:grid-cols-2">
          <div className="inline-flex w-fit rounded-md bg-muted p-1 sm:col-span-2" role="group" aria-label="Request type">
            <Button type="button" size="sm" variant={bulkMode ? "ghost" : "secondary"} aria-pressed={!bulkMode} onClick={() => setBulkMode(false)}>Single student</Button>
            <Button type="button" size="sm" variant={bulkMode ? "secondary" : "ghost"} aria-pressed={bulkMode} onClick={() => setBulkMode(true)}>Multiple students</Button>
          </div>
          {bulkMode ? (
            <div className="space-y-3 sm:col-span-2">
              {bulkStudents.map((student, index) => (
                <div key={index} className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                  <div className="space-y-2">
                    <Label htmlFor={`bulk-student-name-${index}`}>Student {index + 1} name</Label>
                    <Input id={`bulk-student-name-${index}`} required value={student.student_name} onChange={(event) => setBulkStudents((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, student_name: event.target.value } : item))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`bulk-dorm-location-${index}`}>Dorm location</Label>
                    <Input id={`bulk-dorm-location-${index}`} required placeholder="Block / room" value={student.dorm_location} onChange={(event) => setBulkStudents((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, dorm_location: event.target.value } : item))} />
                  </div>
                  <Button type="button" size="icon" variant="ghost" aria-label={`Remove student ${index + 1}`} disabled={bulkStudents.length <= 2} onClick={() => setBulkStudents((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" disabled={bulkStudents.length >= 50} onClick={() => setBulkStudents((current) => [...current, { ...blankBulkStudent }])}>
                <Plus className="h-4 w-4" /> Add student
              </Button>
              <p className="text-xs text-muted-foreground">Parent and outing details below apply to all students in this request.</p>
            </div>
          ) : (
          <div className="space-y-2">
            <Label htmlFor="student_name">Student name</Label>
            <Input id="student_name" required value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} />
          </div>
          )}
          {!bulkMode && (
          <div className="space-y-2">
            <Label htmlFor="dorm_location">Dorm location</Label>
            <Input id="dorm_location" required placeholder="Block / room" value={form.dorm_location} onChange={(e) => setForm({ ...form, dorm_location: e.target.value })} />
          </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="parent_name">Parent / guardian name</Label>
            <Input id="parent_name" required value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="parent_phone">Parent / guardian phone number</Label>
            <Input id="parent_phone" type="tel" required value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="out_start_date">Start date</Label>
            <Input id="out_start_date" type="date" required value={form.out_start_date} onChange={(e) => setForm({ ...form, out_start_date: e.target.value })} data-testid="outing-start-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="out_end_date">End date</Label>
            <Input id="out_end_date" type="date" min={form.out_start_date || undefined} required value={form.out_end_date} onChange={(e) => setForm({ ...form, out_end_date: e.target.value })} data-testid="outing-end-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="out_time">Outing time</Label>
            <Input id="out_time" type="time" required value={form.out_time} onChange={(e) => setForm({ ...form, out_time: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="duration_days">Duration (days)</Label>
            <Input id="duration_days" type="number" min="1" step="1" required value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="purpose">Purpose</Label>
            <Select required value={form.purpose} onValueChange={(purpose) => { setForm({ ...form, purpose }); setAttachmentError(""); }}>
              <SelectTrigger id="purpose" data-testid="outing-purpose-select"><SelectValue placeholder="Choose a purpose" /></SelectTrigger>
              <SelectContent>{OUTING_PURPOSES.map((purpose) => <SelectItem key={purpose} value={purpose}>{purpose}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="outing-description">Detail / description</Label>
            <Textarea id="outing-description" required maxLength={2000} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="outing-description-input" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="outing-attachment">Proof attachment{requiresMedicalProof ? " (required)" : " (optional)"}</Label>
            <Input id="outing-attachment" type="file" required={requiresMedicalProof} accept="application/pdf,image/png,image/jpeg,image/webp,image/gif" onChange={chooseAttachment} data-testid="outing-attachment-input" />
            <p className="text-xs text-muted-foreground">{requiresMedicalProof ? "Attach a medical appointment card or MC / sick leave certificate. PDF or image, max 5 MB." : "PDF or image, max 5 MB."}</p>
            {form.attachment_name && <p className="flex items-center gap-2 text-sm"><Upload className="h-4 w-4" />{form.attachment_name}</p>}
            {attachmentError && <p className="text-sm text-destructive" role="alert">{attachmentError}</p>}
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Submit for approval"}</Button>
          </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Outing rules</DialogTitle>
            <DialogDescription>Read the rules before deciding whether to continue with this request.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[45vh] overflow-y-auto rounded-md border border-border bg-muted/20 p-4">
            <RichTextEditor readOnly value={outingRules} ariaLabel="Outing rules" />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="destructive" disabled={submitting} onClick={() => submitRequest(false)}>
              No, decline
            </Button>
            <Button type="button" disabled={submitting} onClick={() => submitRequest(true)}>
              {submitting ? "Submitting…" : "Yes, I accept"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rulesEditorOpen} onOpenChange={setRulesEditorOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit outing rules</DialogTitle>
            <DialogDescription>Update the message students see before submitting an outing request.</DialogDescription>
          </DialogHeader>
          <RichTextEditor value={rulesDraft} onChange={setRulesDraft} ariaLabel="Outing rules editor" />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRulesEditorOpen(false)}>Cancel</Button>
            <Button type="button" disabled={savingRules} onClick={saveOutingRules}>{savingRules ? "Saving…" : "Save rules"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {canEditRules && (
        <Button
          type="button"
          variant="outline"
          onClick={() => { setRulesDraft(outingRules); setRulesEditorOpen(true); }}
          data-testid="edit-outing-rules-button"
        >
          Edit outing rules
        </Button>
      )}

      <section className="space-y-4 border-t-4 border-emerald-500 pt-4" data-testid="approved-outing-list">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">Approved Outing / BBM</p>
          <h2 className="mt-1 font-display text-xl font-bold">Approved students</h2>
        </div>
        {approved.length === 0 ? (
          <p className="border-y border-border py-5 text-sm text-muted-foreground">No approved outings yet.</p>
        ) : approved.map((outing) => (
          <article key={outing.id} className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid={`approved-outing-${outing.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">{outing.student_name}</h3>
              <Badge className="border-0 bg-emerald-100 text-emerald-800">Approved</Badge>
            </div>
            <OutingSummary outing={outing} />
            <OutingDetails outing={outing} showParentPhone={!isVisitor} reviewedBy={!isVisitor ? outing.decision_by : null} onDownloadAttachment={(outingManager || outing.requester_id === user?.id) ? downloadOutingAttachment : null} />
            {outingManager && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-emerald-200 pt-3">
                <Button size="sm" variant="outline" onClick={() => revertApproval(outing)}><RotateCcw className="mr-1 h-4 w-4" />Revert to pending</Button>
                <Button size="sm" variant="destructive" onClick={() => removeOuting(outing)}><Trash2 className="mr-1 h-4 w-4" />Remove outing</Button>
              </div>
            )}
          </article>
        ))}
      </section>

      {(approver || pending.length > 0 || rejected.length > 0) && (
        <section className="space-y-4 border-t-4 border-amber-500 pt-4">
          <h2 className="font-display text-xl font-bold">{user?.role === "admin" ? "Outing requests" : approver ? "Requests for approval" : "My outing requests"}</h2>
          {pending.length === 0 && rejected.length === 0 ? (
            <p className="border-y border-border py-5 text-sm text-muted-foreground">No pending outing requests.</p>
          ) : [...pending, ...rejected].map((outing) => (
            <article key={outing.id} className="rounded-xl border border-border bg-card p-4" data-testid={`outing-request-${outing.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{outing.student_name}</h3>
                </div>
                <Badge variant={outing.status === "Pending" ? "secondary" : "destructive"}>{outing.status}</Badge>
              </div>
              <OutingSummary outing={outing} />
              <OutingDetails
                outing={outing}
                requesterName={outing.requester_name}
                reviewedBy={outing.status === "Rejected" && outing.terms_accepted !== false ? outing.decision_by : null}
                rejectionNote={outing.status === "Rejected" && outing.terms_accepted === false ? "Rejected because the outing rules were declined." : null}
                onDownloadAttachment={(outingManager || outing.requester_id === user?.id) ? downloadOutingAttachment : null}
              />
              {approver && outing.status === "Pending" && (
                <div className="mt-4 flex gap-2 border-t border-border pt-3">
                  <Button size="sm" onClick={() => decide(outing, "Approved")}><Check className="mr-1 h-4 w-4" />Approve</Button>
                  <Button size="sm" variant="destructive" onClick={() => decide(outing, "Rejected")}><X className="mr-1 h-4 w-4" />Reject</Button>
                </div>
              )}
              {user?.role === "admin" && outing.status === "Rejected" && (
                <div className="mt-4 border-t border-border pt-3">
                  <Button size="sm" variant="destructive" onClick={() => removeOuting(outing)}><Trash2 className="mr-1 h-4 w-4" />Delete request</Button>
                </div>
              )}
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
